import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Spinner,
  VStack,
  HStack,
  IconButton,
  useToast,
  Card,
  CardBody,
  Badge,
  Divider,
  Flex,
  useColorModeValue
} from '@chakra-ui/react';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { generateSentencesForWord, storeSentencesInDatabase } from '../services/tatoeba';

interface Sentence {
  id: string;
  word_id: string;
  sentence: string;
  translation?: string;
  translation_lang?: string;
  created_at?: string;
}

interface Word {
  id: string;
  word: string;
  language: string;
  created_at: string;
}

export default function WordDetail() {
  // Hooks
  const { wordId } = useParams<{ wordId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Get user settings
  const { data: userSettings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      try {
        const data = await apiClient.getUserSettings();
        return data?.settings || { nativeLanguage: 'english' };
      } catch (error) {
        return { nativeLanguage: 'english' };
      }
    },
    enabled: !!user,
  });

  // Fetch word details
  const { data: word, isLoading: isLoadingWord } = useQuery({
    queryKey: ['word-detail', wordId],
    queryFn: async () => {
      if (!wordId) throw new Error('Word ID is required');
      return await apiClient.getWord(wordId) as Word;
    },
    enabled: !!wordId && !!user,
  });

  // Fetch sentences for the word
  const { data: sentences, isLoading: isLoadingSentences } = useQuery({
    queryKey: ['word-sentences', wordId],
    queryFn: async () => {
      if (!wordId) throw new Error('Word ID is required');
      // TODO: Add filtering by wordId in the API endpoint
      const allSentences = await apiClient.getSentences();
      return allSentences.filter((s: any) => s.word_id === wordId).sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ) as Sentence[];
    },
    enabled: !!wordId && !!user,
  });

  // Generate sentences mutation
  const generateSentencesMutation = useMutation({
    mutationFn: async () => {
      if (!word || !wordId) throw new Error('Word is required');

      // Get the user's native language from settings
      const nativeLanguage = userSettings?.nativeLanguage || 'english';

      // Generate sentences
      const generatedSentences = await generateSentencesForWord(
        word.word,
        word.language,
        nativeLanguage
      );

      // Store in database
      if (generatedSentences.length > 0) {
        await storeSentencesInDatabase(wordId, generatedSentences, user?.id || '', word.language);
      }

      return generatedSentences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['word-sentences', wordId] });
      toast({
        title: t('sentences.sentencesGenerated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });

  // Delete sentence mutation
  const deleteSentenceMutation = useMutation({
    mutationFn: async (sentenceId: string) => {
      await apiClient.deleteSentence(parseInt(sentenceId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['word-sentences', wordId] });
      toast({
        title: t('sentences.sentenceDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });

  // Loading state
  if (isLoadingWord || isLoadingSentences) {
    return (
      <Container maxW="container.lg" py={10}>
        <VStack spacing={8} align="center">
          <Spinner size="xl" />
          <Text>{t('common.loading')}</Text>
        </VStack>
      </Container>
    );
  }

  // Word not found
  if (!word) {
    return (
      <Container maxW="container.lg" py={10}>
        <VStack spacing={6}>
          <Heading>{t('words.wordDetails')}</Heading>
          <Text>{t('words.noWords')}</Text>
          <Button
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/dashboard')}
          >
            {t('common.back')}
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={10}>
      <VStack spacing={8} align="stretch">
        <HStack>
          <Button
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/dashboard')}
            variant="outline"
          >
            {t('common.back')}
          </Button>
          <Heading flex="1">{word.word}</Heading>
        </HStack>

        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Text fontWeight="bold">{t('common.language')}:</Text>
                <Badge colorScheme="blue">{word.language}</Badge>
              </HStack>

              <Divider />

              <HStack justify="space-between">
                <Heading size="md">{t('sentences.examples')}</Heading>
                <Button
                  leftIcon={<FaPlus />}
                  colorScheme="brand"
                  size="sm"
                  onClick={() => generateSentencesMutation.mutate()}
                  isLoading={generateSentencesMutation.isPending}
                  loadingText={t('sentences.generatingSentences')}
                >
                  {t('sentences.generateSentences')}
                </Button>
              </HStack>

              {sentences && sentences.length > 0 ? (
                <VStack spacing={4} align="stretch">
                  {sentences.map((sentence) => (
                    <Card key={sentence.id} variant="outline" size="sm">
                      <CardBody>
                        <Flex justify="space-between" align="start">
                          <VStack align="start" spacing={2} flex="1">
                            <Text fontWeight="bold">{sentence.sentence}</Text>
                            {sentence.translation && (
                              <Text color="gray.500">{sentence.translation}</Text>
                            )}
                          </VStack>
                          <IconButton
                            aria-label={t('common.delete')}
                            icon={<FaTrash />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => deleteSentenceMutation.mutate(sentence.id)}
                          />
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              ) : (
                <Box textAlign="center" py={8}>
                  <Text>{t('sentences.noSentences')}</Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
}